// src/pages/ManageStaffPage.tsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { useTranslation } from "react-i18next";
import MembershipSwitcher from "../MembershipSwitcher";
import { useTheme } from "../../../context/ThemeContext";
import { useTenantFormat } from "../../../hooks/useTenantFormat";
import { isFutureInvoice, localTodayYmd } from "../../../utils/invoiceDueDate";
import { getPaymentErrorMessage } from "../../../utils/paymentErrors";
import ChargeStatusBadge from "./ChargeStatusBadge";
import { summarizeUpcoming } from "../../../utils/upcomingTotals";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
} from "../../../utils/themeUtils";
import {
  deleteInvoice,
  getInvoicesByMember,
  getFutureInvoicesByMember,
  updateBillingHistoryInvoice,
  reorderBillingHistory,
} from "../../../api/invoiceAPI";
import Tooltip from "../../common/Tooltip";
import ActionMenu from "../../common/ActionMenu";
// DnD imports for reorder feature
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectMembershipDetails,
  setMemberInvoices,
  setMembership,
} from "../../../store/slices/membershipSlice";
import BalanceDisplay from "../../payment/BalanceDisplay";
import PaymentModal from "../../payment/PaymentModal";
import { Button, Card, Input, PageContainer, PageHeading, SectionHeading, Select, TD, TH, THead, Table } from "../../ui";
import {
  getMemberById,
  getMembership,
  updateMembershipDetails,
  patchMembershipDetails,
  recalculateMembershipBalance,
} from "../../../api/membershipAPI";
import { RefreshCw } from "lucide-react";
import MemberInvoiceTooltip from "../../invoice/membership/MemberInvoiceTooltip";
import { getScheduledPayments } from "../../../api/paymentsAPI";
import UpdateCardForm from "../../../components/membership/UpdateCardForm";
import MemberFutureInvoices from "../MemberFutureInvoices";
import BillingPager from "./BillingPager";
import {
  defaultHistoryPageSize,
  pageSizeOptions,
  unpaidChargesOffPage,
} from "./billingPaging";
import MemberScheduledPayments from "../MemberScheduledPayments";
import MessagesHistoryPage from "../MessagesHistoryPage";
import { balanceStatusKey, BALANCE_STATUS_DEFAULTS } from "../../../utils/balanceStatus";
import {
  selectIsAdmin,
  selectIsMember,
  selectIsStaff,
} from "../../../store/slices/authSlice";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import CreateManualInvoice from "../../../components/staff/invoice/CreateManualInvoice";
import Modal from "../../common/Modal";
import SelectAdvancePaymentMethodInMember from "../../invoice/staff/modal/SelectAdvancePaymentMethodInMember";
import {
  ResponsibleCell,
  SharedAccountNote,
  isPaymentRow,
  paidByLabel,
  showPeopleColumns,
  useAccountPeople,
} from "./SharedAccountBilling";
import InvoiceActions from "../../invoice/staff/action/InvoiceActions";
import ConfirmDeleteModal from "../../common/ConfirmDeleteModal";
import EditBillingInvoiceModal from "../EditBillingInvoiceModal";
import RefundModal from "../../payment/RefundModal";
import { useViewportFlags } from "../../../hooks/useViewportFlags";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { persistAuthToken } from "../../../api/authTokenStore";
import { exportCsv } from "../../../utils/csvExport";

interface Props {
  widthClass?: string;
  source?: string;
  /**
   * Set by the staff membership page, which draws Billing History and Payment
   * Settings in its own tab row beside Notes, Messages and Tasks. When given,
   * this component shows that tab's content and does not draw its own tab strip.
   */
  billingTab?: "history" | "payment";
  membershipId?: string;
  /**
   * Staff membership page only: opens Payment Settings, where the Payment
   * Schedule lives. Offered from the note above the table on a shared account.
   */
  onOpenPaymentSchedule?: () => void;
}

interface InvoicePayment {
  id?: string;
  amount: number;
  type?: string;
  status?: string;
  payment_method?: string;
  payment_date?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

// Loose shape — runtime data has many optional fields and most internal
// translators accept any string. Tightening this further would surface real
// undefined-handling bugs across the page that are out of scope for the
// any-reduction pass.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BillingInvoice = any;

// Build a display string from manual-invoice line items so the row shows
// what the invoice was actually for, instead of the generic
// "Manual invoice from staff". Filters out the synthetic discount line
// (unitPrice <= 0) the backend appends to make the math add up.
const formatInvoiceLineItems = (invoice: BillingInvoice): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = invoice?.metadata?.lineItems || invoice?.lineItems || [];
  if (!Array.isArray(items) || items.length === 0) return "";
  const descs = items
    .filter((i) => i?.description && Number(i?.unitPrice ?? 0) > 0)
    .map((i) => {
      const qty = Number(i.quantity) || 1;
      return qty > 1 ? `${qty}× ${i.description}` : String(i.description);
    });
  return descs.join(", ");
};

// Compose primary text + optional line-items subtext for an invoice
// row description cell. Used by all four render sites (desktop row,
// mobile card, paginated desktop, paginated mobile) so they stay in
// sync. The primary text is the canonical description, which means
// staff on a stale bundle still see the same first line as staff on
// the latest bundle — the line-items subtext just adds detail when
// the new code path is loaded.
interface InvoiceRowDescriptionProps {
  invoice: BillingInvoice;
  isDarkMode: boolean;
  translateDescription: (desc: string) => string;
  translateInvoiceType: (type: string) => string;
}

const InvoiceRowDescription: React.FC<InvoiceRowDescriptionProps> = ({
  invoice,
  isDarkMode,
  translateDescription,
  translateInvoiceType,
}) => {
  const lineItemsText = formatInvoiceLineItems(invoice);
  const primary =
    translateDescription(invoice.description) ||
    translateInvoiceType(invoice.metadata?.display_invoice_type) ||
    lineItemsText;
  const showSubtext = lineItemsText && lineItemsText !== primary;
  const fullTitle = showSubtext ? `${primary} — ${lineItemsText}` : primary;
  // `truncate` doesn't work on a flex container (flex overrides the
  // `display: block` it needs). Each child <span> handles its own
  // truncation; the outer wrapper just needs `min-w-0` so flex
  // children can shrink below their intrinsic width inside a parent
  // with a capped max-width.
  return (
    <span className="flex flex-col min-w-0" title={fullTitle}>
      <span className="truncate">{primary}</span>
      {showSubtext && (
        <span
          className={`truncate text-xs ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          {lineItemsText}
        </span>
      )}
    </span>
  );
};

// Sortable Table Row Component for Desktop
interface SortableTableRowProps {
  invoice: BillingInvoice;
  isReorderMode: boolean;
  isDarkMode: boolean;
  isCredit: boolean;
  isDebit: boolean;
  getPrimaryText: (dark: boolean) => string;
  getPaymentMethodColor: (method: string) => string;
  translatePaymentMethod: (method: string) => string;
  translateDescription: (desc: string) => string;
  translateInvoiceType: (type: string) => string;
  t: (key: string) => string;
  onRowClick: (e: React.MouseEvent, invoice: BillingInvoice) => void;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
  invoice,
  isReorderMode,
  isDarkMode,
  isCredit,
  isDebit,
  getPrimaryText,
  getPaymentMethodColor,
  translatePaymentMethod,
  translateDescription,
  translateInvoiceType,
  t,
  onRowClick,
}) => {
  const { formatCurrency } = useTenantFormat();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: invoice.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <tr
      ref={setNodeRef}
      style={style as React.CSSProperties}
      className={`group relative ${
        isCredit
          ? isDarkMode
            ? "bg-green-900"
            : "bg-green-100"
          : isDebit
          ? isDarkMode
            ? "bg-red-900"
            : "bg-red-200"
          : isDarkMode
          ? "bg-gray-800"
          : "bg-white"
      } border-b ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      } ${isReorderMode ? "cursor-grab" : "cursor-pointer"} ${
        isDragging ? "shadow-lg" : ""
      }`}
      onClick={(e) => !isReorderMode && onRowClick(e, invoice)}
    >
      {/* Drag Handle Column - Only in reorder mode */}
      {isReorderMode && (
        <TD
          className="w-10"
          {...attributes}
          {...listeners}
        >
          <GripVertical
            size={18}
            className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} cursor-grab active:cursor-grabbing`}
          />
        </TD>
      )}

      {/* Description — capped width with truncation. Without max-w, the
          column expands to fit the longest line item ("Camp
          Registration: Gymnastics Camp - Fun Theme for this week is
          'Candy Land' - June 15th thro 19th 2026") and pushes the
          Credit/Debit/Balance/Date columns off-screen on non-wide
          monitors. Reported by Chris @ GoY 2026-05-10 — staff couldn't
          see balance/date on the office's regular monitor. Full text
          remains accessible via title-attribute hover. */}
      <TD
        className={`max-w-md ${getPrimaryText(isDarkMode)}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {/* Payment Method Tag (for payments) */}
          {invoice?.metadata?.display_payment_method && (
            <>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getPaymentMethodColor(invoice.metadata.display_payment_method)}`}>
                {translatePaymentMethod(invoice.metadata.display_payment_method)}
              </span>
              <span className="truncate min-w-0" title={translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}>
                {translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}
              </span>
            </>
          )}
          {/* The "Charged to X" badge that sat here is gone (2026-10-08). It
              only ever drew while reordering, and it named whose turn it was
              to pay even when the account's own card was the one charged.
              Who paid is the Paid by column; whose turn it was is the
              Responsible column. */}
          {/* Invoice Badge and Description (for invoices). See
              InvoiceRowDescription for how primary + subtext are
              composed and why. */}
          {!invoice?.metadata?.display_payment_method && (
            <>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                {t('membership:billing.invoice')}
              </span>
              <ChargeStatusBadge invoice={invoice} isDarkMode={isDarkMode} />
              <div className="min-w-0 flex-1">
                <InvoiceRowDescription
                  invoice={invoice}
                  isDarkMode={isDarkMode}
                  translateDescription={translateDescription}
                  translateInvoiceType={translateInvoiceType}
                />
              </div>
            </>
          )}
        </div>
      </TD>

      {/* Credit */}
      <TD
        className={`${
          isDarkMode ? "text-green-400" : "text-green-600"
        }`}
      >
        {invoice?.metadata?.credit ? (
          <div>+{formatCurrency(invoice.metadata.credit)}</div>
        ) : (
          <>&nbsp; {formatCurrency(0)}</>
        )}
      </TD>

      {/* Debit */}
      <TD
        className={`${
          isDarkMode ? "text-red-400" : "text-red-600"
        }`}
      >
        {invoice?.metadata?.debit ? (
          <div>
            -{formatCurrency(Number(invoice?.metadata?.debit))}
          </div>
        ) : !isCredit && invoice?.amount ? (
          <div>-{formatCurrency(Number(invoice?.amount))}</div>
        ) : (
          <>{formatCurrency(0)}</>
        )}
      </TD>

      {/* Balance */}
      <TD
        className={`font-medium ${getPrimaryText(isDarkMode)}`}
      >
        {invoice?.metadata?.previous_balance !== undefined ? (
          (() => {
            const prev = Number(invoice.metadata.previous_balance) || 0;
            const credit =
              invoice.metadata.credit !== undefined
                ? Number(invoice.metadata.credit)
                : invoice.payments
                    ?.filter((p: InvoicePayment) => p.type === "credit")
                    ?.reduce(
                      (sum: number, p: InvoicePayment) => sum + Number(p.amount),
                      0
                    ) ?? 0;
            const debit =
              invoice.metadata.debit !== undefined
                ? Number(invoice.metadata.debit)
                : invoice?.status === "outstanding" && invoice?.amount
                ? Number(invoice.amount)
                : invoice.payments
                    ?.filter((p: InvoicePayment) => p.type === "debit")
                    ?.reduce(
                      (sum: number, p: InvoicePayment) => sum + Number(p.amount),
                      0
                    ) ?? 0;

            let remaining = prev;
            if (invoice.metadata.type === "credit") {
              remaining += credit;
            } else if (
              invoice.metadata.type === "debit" ||
              (invoice?.status === "outstanding" && invoice?.amount)
            ) {
              remaining -= debit;
            }

            return <span>{formatCurrency(remaining)}</span>;
          })()
        ) : (
          <span>—</span>
        )}
      </TD>

      {/* Date */}
      <TD
        className={`${getPrimaryText(isDarkMode)}`}
      >
        {formatDateOnly(invoice?.due_date)}
      </TD>

      {/* Hidden Actions Column */}
      <TD className="hidden">
        {/* Actions hidden */}
      </TD>
    </tr>
  );
};

// Sortable Mobile Card Component
interface SortableMobileCardProps {
  invoice: BillingInvoice;
  isReorderMode: boolean;
  isDarkMode: boolean;
  isCredit: boolean;
  isDebit: boolean;
  isMobileLandscape: boolean;
  credit: number;
  debitAmount: number;
  balance: number;
  getPrimaryText: (dark: boolean) => string;
  getSecondaryText: (dark: boolean) => string;
  getMutedText: (dark: boolean) => string;
  getPaymentMethodColor: (method: string) => string;
  translatePaymentMethod: (method: string) => string;
  translateDescription: (desc: string) => string;
  translateInvoiceType: (type: string) => string;
  t: (key: string) => string;
  onCardClick: (e: React.MouseEvent, invoice: BillingInvoice) => void;
}

const SortableMobileCard: React.FC<SortableMobileCardProps> = ({
  invoice,
  isReorderMode,
  isDarkMode,
  isCredit,
  isDebit,
  isMobileLandscape,
  credit,
  debitAmount,
  balance,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getPaymentMethodColor,
  translatePaymentMethod,
  translateDescription,
  translateInvoiceType,
  t,
  onCardClick,
}) => {
  // One money format on this page: negatives read -$10.00, never $-10.00.
  const { formatCurrency } = useTenantFormat();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: invoice.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style as React.CSSProperties}
      className={`rounded-lg border-2 shadow-sm transition-all duration-200 touch-manipulation ${
        isCredit
          ? "bg-green-50 border-green-200"
          : isDebit
          ? "bg-red-50 border-red-200"
          : "bg-white border-gray-200"
      } ${isMobileLandscape ? "p-3 min-h-[140px]" : "p-4"} ${
        isReorderMode ? "cursor-grab" : "cursor-pointer"
      } ${isDragging ? "shadow-lg" : ""}`}
      onClick={(e) => !isReorderMode && onCardClick(e, invoice)}
    >
      {/* Header with Drag Handle, Invoice ID and Amount */}
      <div
        className={`flex items-start justify-between ${
          isMobileLandscape ? "mb-2" : "mb-3"
        }`}
      >
        <div className="flex-1 flex items-start gap-2">
          {/* Drag Handle - Only in reorder mode */}
          {isReorderMode && (
            <div
              className="flex-shrink-0 pt-1"
              {...attributes}
              {...listeners}
            >
              <GripVertical
                size={20}
                className={`${isDarkMode ? "text-gray-400" : "text-gray-500"} cursor-grab active:cursor-grabbing`}
              />
            </div>
          )}
          <div className="flex-1">
            <div
              className={`flex items-center space-x-2 ${
                isMobileLandscape ? "mb-1" : "mb-2"
              }`}
            >
              <div
                className={`rounded-full flex items-center justify-center ${
                  isCredit
                    ? "bg-green-500"
                    : isDebit
                    ? "bg-red-500"
                    : "bg-blue-500"
                } ${isMobileLandscape ? "w-6 h-6" : "w-8 h-8"}`}
              >
                <svg
                  className={`text-white ${
                    isMobileLandscape ? "w-3 h-3" : "w-4 h-4"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p
                  className={`font-semibold ${getPrimaryText(isDarkMode)} ${
                    isMobileLandscape ? "text-sm" : "text-base"
                  }`}
                >
                  ID: {invoice?.id?.slice(0, 8)}...
                </p>
                <p
                  className={`${getMutedText(isDarkMode)} ${
                    isMobileLandscape ? "text-xs" : "text-sm"
                  }`}
                >
                  {formatDateOnly(invoice?.due_date)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className={`text-right ${isMobileLandscape ? "ml-2" : "ml-4"}`}>
          <p
            className={`font-bold ${
              isMobileLandscape ? "text-sm" : "text-lg"
            } ${getPrimaryText(isDarkMode)}`}
          >
            {formatCurrency(balance)}
          </p>
          <p
            className={`${getMutedText(isDarkMode)} ${
              isMobileLandscape ? "text-xs" : "text-sm"
            }`}
          >
            {t('membership:billing.balance')}
          </p>
        </div>
      </div>

      {/* Description */}
      <div className={`${isMobileLandscape ? "mb-2" : "mb-3"}`}>
        <div className="flex items-start gap-2 flex-wrap">
          {/* Payment Method Tag (for payments) */}
          {invoice?.metadata?.display_payment_method && (
            <>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getPaymentMethodColor(invoice.metadata.display_payment_method)}`}>
                {translatePaymentMethod(invoice.metadata.display_payment_method)}
              </span>
              <p
                className={`${getSecondaryText(isDarkMode)} leading-relaxed ${
                  isMobileLandscape ? "text-sm line-clamp-2" : "text-base"
                }`}
              >
                {translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}
              </p>
            </>
          )}
          {/* Invoice Badge and Description (for invoices) */}
          {!invoice?.metadata?.display_payment_method && (
            <>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                {t('membership:billing.invoice')}
              </span>
              <ChargeStatusBadge invoice={invoice} isDarkMode={isDarkMode} />
              <div
                className={`${getSecondaryText(isDarkMode)} leading-relaxed ${
                  isMobileLandscape ? "text-sm line-clamp-2" : "text-base"
                }`}
              >
                <InvoiceRowDescription
                  invoice={invoice}
                  isDarkMode={isDarkMode}
                  translateDescription={translateDescription}
                  translateInvoiceType={translateInvoiceType}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Credit/Debit Row */}
      <div
        className={`grid grid-cols-2 gap-4 ${
          isMobileLandscape ? "text-sm" : "text-base"
        }`}
      >
        {/* Credit */}
        <div
          className={`flex items-center justify-between bg-green-100 border border-green-300 rounded-lg ${
            isMobileLandscape ? "p-2" : "p-3"
          }`}
        >
          <span className="text-green-800 font-medium">
            {t('membership:billing.credit')}
          </span>
          <span className="text-green-700 font-bold">
            {credit > 0 ? `+${formatCurrency(credit)}` : formatCurrency(0)}
          </span>
        </div>

        {/* Debit */}
        <div
          className={`flex items-center justify-between bg-red-100 border border-red-300 rounded-lg ${
            isMobileLandscape ? "p-2" : "p-3"
          }`}
        >
          <span className="text-red-800 font-medium">
            {t('membership:billing.debit')}
          </span>
          <span className="text-red-700 font-bold">
            {debitAmount > 0 ? `-${formatCurrency(debitAmount)}` : formatCurrency(0)}
          </span>
        </div>
      </div>
    </div>
  );
};

/** Several "reload" requests inside this window become one reload. */
export const BILLING_REFRESH_DEBOUNCE_MS = 400;

const MemberBillingHistory: React.FC<Props> = ({
  widthClass,
  source,
  membershipId: propMembershipId,
  billingTab,
  onOpenPaymentSchedule,
}) => {
  // One money format on this page: negatives read -$10.00, never $-10.00.
  const { formatCurrency } = useTenantFormat();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation(['membership', 'common']);
  const [refreshApiCalls, setRefreshApiCalls] = useState<number>(0);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  // Member self-pay modal (the "Make a Payment" button on the unified Account
  // Balance card — same flow BalanceDisplay used before the cards were unified).
  const [showMemberPayModal, setShowMemberPayModal] = useState<boolean>(false);
  // Billing tabs (EditMembershipPage only) — Account Balance pinned above a
  // Billing History | Payment Settings tab set.
  // `?tab=payment` opens Payment Settings directly: the "add a payment
  // method" email links to the member's billing page with it. Only on the
  // member's own billing page (no `source`), so it cannot collide with the
  // staff membership page's own tab parameters.
  const [ownBillingTab, setActiveBillingTab] = useState<"history" | "payment">(() =>
    !source &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("tab") === "payment"
      ? "payment"
      : "history"
  );
  const activeBillingTab = billingTab ?? ownBillingTab;
  const [invoices, setInvoices] = useState<any[]>([]); // State to store staff data
  const [futureInvoicesTotal, setFutureInvoicesTotal] = useState<number>(0); // ✅ Track future invoices total
  const [scheduledPaymentsTotal, setScheduledPaymentsTotal] = useState<number>(0); // ✅ Track scheduled payments total
  // Shown apart from the totals above: drafts are not sent yet, and a failed
  // scheduled payment is not money on its way (summarizeUpcoming).
  const [upcomingDrafts, setUpcomingDrafts] = useState<{ total: number; count: number }>({ total: 0, count: 0 });
  const [failedScheduled, setFailedScheduled] = useState<{ total: number; count: number }>({ total: 0, count: 0 });

  // Loading state
  const [isCreatedManualInvoice, setIsCreatedmanualInvoice] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState(false);
  const [, setIsloading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState<boolean>(false);
  // Critical billing highlight note (admin/staff). A single prominent note
  // rendered right by the Accept Payment / Create Invoice actions so the
  // staffer sees any per-member caveat ("paying by check", "hold new
  // invoices", "past-due — collect first") at the moment they'd act. Saved to
  // memberships.billing_highlight_note via PATCH. Distinct from the Billing
  // Notes feed, which stays the running log of updates.
  const [highlightNote, setHighlightNote] = useState<string>("");
  const [savingHighlight, setSavingHighlight] = useState<boolean>(false);
  const highlightSavedRef = useRef<string>("");
  // Error state
  const [error, setError] = useState<string | null>(null);

  // pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 3 rows on the staff membership page, 10 everywhere else (see billingPaging).
  const [itemsPerPage, setItemsPerPage] = useState<number>(() => defaultHistoryPageSize(source));

  // Active program enrollments — drives the context strip above
  // the invoice table so staff can verify "what are these dues FOR?"
  // without switching to the Enrollments tab. See 2026-05-17 "also"
  // report where Stacy couldn't tell if Serenity was billed at the
  // 1-hour or 2-hour rate.
  const [activeEnrollments, setActiveEnrollments] = useState<Array<{
    enrollment_id: string;
    athlete_name: string;
    program_label: string;
    schedule_label: string;
    duration_hours: number | null;
  }>>([]);
  const [tooltipData, setTooltipData] = useState<null | {
    x: number;
    y: number;
    invoice: BillingInvoice;
  }>(null);
  const { isMobileDevice, isMobileLandscape } = useViewportFlags();
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] =
    useState<boolean>(false);

  // Selected id states
  const [invoiceToDelete, setInvoiceToDelete] = useState<BillingInvoice | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<BillingInvoice | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState<BillingInvoice | null>(null);

  // REORDER MODE STATE
  const [isReorderMode, setIsReorderMode] = useState<boolean>(false);
  const [reorderedInvoices, setReorderedInvoices] = useState<any[]>([]);
  const [, setOriginalInvoices] = useState<any[]>([]);
  const [hasUnsavedReorderChanges, setHasUnsavedReorderChanges] = useState<boolean>(false);
  const [isSavingReorder, setIsSavingReorder] = useState<boolean>(false);

  // HELPER
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reduxMembership: any = useSelector(selectMembershipDetails);
  // Use prop membershipId if provided, otherwise fall back to Redux
  const effectiveMembershipId = propMembershipId || reduxMembership?.id;
  const membership = reduxMembership || { id: propMembershipId, balance: 0 };
  const isMember = useSelector(selectIsMember);
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);
  const hasRefundPermission = useHasPermission("refund_payments");
  const token = localStorage.getItem("authToken");
  const dispatch = useDispatch();
  const location = useLocation();
  const shouldHideMessages = location.pathname.endsWith("/member/view-billing");

  // Check if user can reorder (admin or central auth)
  const authSource = localStorage.getItem("authSource");
  // Tenant owners / platform staff authenticate against the CENTRAL DB and have
  // NO tenant `users` row, so `isAdmin`/`isStaff` (derived from localStorage
  // userRole) are unreliable for them — exactly why the backend permission gate
  // short-circuits on `source === 'central' || 'tenant_owner'`. Mirror that here
  // so central-auth owners get the invoice Edit/Delete/Refund actions. Without
  // this, Chris @ GoY couldn't edit a refunded camp invoice to $0 or delete it
  // ("neither appears to be an option" — 2026-06-10). Same root cause as the
  // navbar getUserDetail 404 and the existing canReorder workaround below.
  const rawUserRole = (localStorage.getItem("userRole") || "")
    .toLowerCase()
    .replace(/\s+/g, "_");
  const isCentralOwner =
    authSource === "central" ||
    authSource === "tenant_owner" ||
    rawUserRole === "tenant_owner" ||
    rawUserRole === "super_admin" ||
    rawUserRole === "platform_admin";
  const canManageBilling = isAdmin || isStaff || isCentralOwner;
  // Refunds are their own permission on the server (`refund_payments`), not
  // part of managing billing: staff and managers who can edit an invoice got a
  // 403 when they tried. Offer the button only to people the server lets refund.
  const canRefund = hasRefundPermission || isCentralOwner;
  const canReorder = (isAdmin || isCentralOwner) && source === "EditMembershipPage";

  // Shared accounts: who was responsible for each invoice and who paid.
  // `peopleColumns` is false for an account with one person and no names on
  // any row, and then nothing below draws: the table is what it always was.
  const accountPeople = useAccountPeople(token, effectiveMembershipId);
  const peopleColumns = showPeopleColumns(accountPeople, invoices);
  const canEditResponsible = canManageBilling && source === "EditMembershipPage";
  // Shown ONLY in the pop-up a row opens. The table and the phone cards carry
  // no Responsible or Paid by (owner, 2026-10-09: "supposed to display only in
  // the modal that opens when you click on the row").
  const peopleDetailProps = (shown: BillingInvoice) => {
    if (!peopleColumns) return {};
    // The pop-up holds the row as it was when clicked; read the current one so
    // a name staff just saved shows without closing and reopening.
    const invoice = invoices?.find((inv) => inv.id === shown?.id) || shown;
    if (isPaymentRow(invoice)) return { sharedPaidBy: paidByLabel(invoice) };
    return {
      responsible: (
        <ResponsibleCell
          invoice={invoice}
          people={accountPeople}
          canEdit={canEditResponsible}
          token={token}
          onSaved={() => setRefreshApiCalls((prev) => prev + 1)}
        />
      ),
      sharedNote: (
        <SharedAccountNote
          forStaff={canEditResponsible}
          onOpenPaymentSchedule={canEditResponsible ? onOpenPaymentSchedule : undefined}
        />
      ),
    };
  };

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor)
  );

  // Helper function to get payment method badge color
  const getPaymentMethodColor = (method: string): string => {
    const colors: { [key: string]: string } = {
      'Cash': isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      'Card': isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
      'Check': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Cheque': isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      'Bank Account': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'Bank Account (ACH)': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      'ACH': isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
    };
    return colors[method] || (isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800');
  };

  // Helper function to translate payment methods
  const translatePaymentMethod = (method: string): string => {
    const methodMap: { [key: string]: string } = {
      'Cash': t('membership:billing.paymentMethods.cash'),
      'Card': t('membership:billing.paymentMethods.card'),
      'Check': t('membership:billing.paymentMethods.check'),
      'Cheque': t('membership:billing.paymentMethods.cheque'),
      'Bank Account': t('membership:billing.paymentMethods.bankAccount'),
      'Bank Account (ACH)': t('membership:billing.paymentMethods.bankAccountACH'),
      'ACH': t('membership:billing.paymentMethods.ach'),
    };
    return methodMap[method] || method;
  };

  // Helper function to translate invoice types
  const translateInvoiceType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'Membership Dues': t('membership:billing.invoiceTypes.membershipDues'),
      'Manual Invoice': t('membership:billing.invoiceTypes.manualInvoice'),
    };
    return typeMap[type] || type;
  };

  // Helper function to translate common invoice description patterns
  const translateDescription = (description: string): string => {
    if (!description) return description;

    // Match "Invoice #XXXXX" pattern
    const invoiceNumberMatch = description.match(/^Invoice #(\d+)$/i);
    if (invoiceNumberMatch) {
      return t('membership:billing.invoiceTypes.invoiceNumber', { number: invoiceNumberMatch[1] });
    }

    // Match common static descriptions
    const descriptionMap: { [key: string]: string } = {
      'Manual invoice from staff': t('membership:billing.invoiceTypes.manualInvoiceFromStaff'),
    };

    return descriptionMap[description] || description;
  };

  // Get Invoices By Member
  const fetch = async () => {
    setFetchLoading(true);
    try {
      const response = await getInvoicesByMember(token, effectiveMembershipId);

      if (response?.success) {
        setInvoices(response?.data);
        dispatch(setMemberInvoices(response?.data));
        persistAuthToken(response?.token);
      } else {
        // Handle case when no invoices exist (404 response)
        // Clear the invoices to ensure UI updates
        setInvoices([]);
        dispatch(setMemberInvoices([]));
      }
    } catch (error: unknown) {
      setError((error as Error)?.message || "Failed to load invoices");
      // Also clear invoices on error
      setInvoices([]);
      dispatch(setMemberInvoices([]));
    } finally {
      setLoading(false);
      setFetchLoading(false); // Always reset loading indicator
    }
  };

  const fetchMembershipDetails = async () => {
    try {
      const response = await getMemberById(token, effectiveMembershipId);

      // Assuming the membership details have a field `id`
      if (response && response.data) {
        dispatch(setMembership(response?.data));
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
    }
  };

  // ✅ Fetch future invoices and calculate total
  const fetchFutureInvoicesTotal = async () => {
    try {
      const response = await getFutureInvoicesByMember(token, effectiveMembershipId);

      if (response?.success && response?.data) {
        // Published future charges only; drafts are counted apart.
        const upcoming = summarizeUpcoming(response.data, []);
        setFutureInvoicesTotal(upcoming.chargesTotal);
        setUpcomingDrafts({ total: upcoming.draftsTotal, count: upcoming.draftsCount });
      } else {
        setFutureInvoicesTotal(0);
        setUpcomingDrafts({ total: 0, count: 0 });
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
      setFutureInvoicesTotal(0);
      setUpcomingDrafts({ total: 0, count: 0 });
    }
  };

  // ✅ Fetch scheduled payments and calculate total
  const fetchScheduledPaymentsTotal = async () => {
    try {
      const response = await getScheduledPayments(token, effectiveMembershipId);

      if (response?.success && response?.data) {
        // Only payments that will still run. A FAILED one used to be counted
        // here as money on its way; it is shown apart instead.
        const upcoming = summarizeUpcoming([], response.data);
        setScheduledPaymentsTotal(upcoming.scheduledTotal);
        setFailedScheduled({ total: upcoming.failedTotal, count: upcoming.failedCount });
      } else {
        setScheduledPaymentsTotal(0);
        setFailedScheduled({ total: 0, count: 0 });
      }
    } catch (err) {
      // Axios interceptor handles Sentry capture
      setScheduledPaymentsTotal(0);
      setFailedScheduled({ total: 0, count: 0 });
    }
  };

  // CLUB-CENTRAL-TENANT-3T fix (2026-06-03): the bare `[]` effect used
  // to fire on mount before Redux had hydrated reduxMembership, so
  // effectiveMembershipId was `undefined` and getMemberById built
  // `/api/memberships/undefined` → 404. Guard + dep matches the
  // pattern of the future-invoices effect just below.
  useEffect(() => {
    if (effectiveMembershipId) {
      fetchMembershipDetails();
    }
  }, [effectiveMembershipId]);

  // ✅ Fetch future invoices and scheduled payments when membership is available
  useEffect(() => {
    if (effectiveMembershipId) {
      fetchFutureInvoicesTotal();
      fetchScheduledPaymentsTotal();
    }
  }, [effectiveMembershipId]);

  // HOW THIS PAGE STAYS CURRENT (2026-10-08). It does NOT update live, and
  // it no longer pretends to.
  //
  // It used to open three database-change channels (invoices, payments,
  // scheduled_payments). None of them could ever deliver anything:
  //   - none of the three tables is in the realtime publication;
  //   - the browser holds the club's public key with no sign-in, and these
  //     tables are readable by the server only, so even a published table
  //     would send this page no rows (and must not: opening billing rows to
  //     the public key to get live updates is not a trade worth making);
  //   - the payments channel filtered on `user_id`, a column payments does
  //     not have.
  // Each channel still reported "subscribed", held a websocket topic open,
  // and retried when it dropped.
  //
  // What actually refreshes the page, all of which were already here or are
  // made complete below:
  //   - every action taken ON this page (payment, edit, refund, delete,
  //     reorder, responsible person) reloads it when it finishes;
  //   - coming back to the tab or window reloads the history AND the two
  //     totals beside it (see the effect below);
  //   - the balance re-reads once a minute while the tab is visible.
  // A change made by someone else while this tab stays in front shows on the
  // next of those. `refreshBilling` is the one "reload everything" used by
  // the focus handlers; a burst of them collapses into one reload.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshBilling = React.useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refreshTimer.current = null;
      if (!effectiveMembershipId) return;
      fetchMembershipDetails();
      fetch();
      fetchFutureInvoicesTotal();
      fetchScheduledPaymentsTotal();
    }, BILLING_REFRESH_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMembershipId]);
  useEffect(() => () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);

  // Load active enrollments for the context strip. Non-blocking —
  // billing history still renders if this fails. Refetches when the
  // selected membership changes (multi-family switcher).
  useEffect(() => {
    if (!effectiveMembershipId) return;
    const tokenLocal = localStorage.getItem("authToken");
    if (!tokenLocal) return;
    let cancelled = false;
    import("../../../api/membershipAPI").then(({ getActiveEnrollments }) => {
      getActiveEnrollments(tokenLocal, effectiveMembershipId)
        .then((res) => {
          if (cancelled) return;
          if (res?.success && Array.isArray(res.data)) {
            setActiveEnrollments(res.data);
          } else {
            setActiveEnrollments([]);
          }
        })
        .catch(() => setActiveEnrollments([]));
    });
    return () => {
      cancelled = true;
    };
  }, [effectiveMembershipId]);

  // ✅ Refetch future invoices and scheduled payments when refreshApiCalls changes
  useEffect(() => {
    if (refreshApiCalls === 0) return; // Skip initial mount
    fetchFutureInvoicesTotal();
    fetchScheduledPaymentsTotal();
  }, [refreshApiCalls]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // edit handler (navigate or open modal)
  const handleEdit = (invoiceId: string) => {
    if (canManageBilling) {
      const invoice = invoices?.find((inv) => inv.id === invoiceId);
      setSelectedInvoice(invoice);
      setIsEditModalOpen(true);
    }
  };

  // Delete invoice handler
  const handleDelete = async () => {
    try {
      if (invoiceToDelete) {
        setDeleting(true);
        const res = await deleteInvoice(token, invoiceToDelete?.id);

        if (res?.success) {
          // Show success immediately — don't wait for refresh
          setShowDeleteModal(false);
          setInvoiceToDelete(null);
          setDeleting(false);
          toast.success("Invoice Deleted");

          // Refresh data in background (timeout won't affect user feedback)
          fetch().catch(() => {});
          fetchMembershipDetails().catch(() => {});
        } else {
          toast.error("Failed to delete invoice. Please try again.");
        }
      }
    } catch (error: unknown) {
      // The server says why a line cannot be deleted (a card payment Stripe
      // holds must be refunded; a line held by a live registration). Show
      // that, not axios's "Request failed with status code 409".
      const serverMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(serverMessage || "Failed to delete invoice: " + ((error as Error)?.message || "Unknown error"));
    } finally {
      setDeleting(false);
    }
  };

  // Keep the highlight-note field in sync with the loaded membership.
  useEffect(() => {
    const current = membership?.billing_highlight_note ?? "";
    setHighlightNote(current);
    highlightSavedRef.current = current;
  }, [membership?.id, membership?.billing_highlight_note]);

  // Save the billing highlight note on blur (only when it actually changed).
  const handleHighlightNoteBlur = async () => {
    const next = highlightNote.trim();
    if (next === (highlightSavedRef.current ?? "").trim()) return; // no change
    if (!membership?.id) return;

    setSavingHighlight(true);
    try {
      const res = await patchMembershipDetails(token, membership.id, {
        billing_highlight_note: next || null,
      });
      if (res?.success) {
        highlightSavedRef.current = next;
        if (res?.data) dispatch(setMembership(res.data));
        toast.success("Billing highlight saved");
      } else {
        toast.error(res?.error || "Failed to save billing highlight");
      }
    } catch (err) {
      toast.error("Failed to save billing highlight");
    } finally {
      setSavingHighlight(false);
    }
  };

  // Annual registration month updated
  const handleAnnualMonthChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMonth = e.target.value;

    try {
      const res = await updateMembershipDetails(token, membership?.id, {
        annual_registration_month: selectedMonth,
      });
      if (res?.status === "success") {
        dispatch(setMembership(res?.data));
        toast.success("Annual registration month updated!");
      }
    } catch (err) {
      toast.error("Failed to update month: " + err);
    }
  };

  // Recalculate balance
  const handleRecalculateBalance = async () => {
    if (!effectiveMembershipId) {
      toast.error("No membership ID found");
      return;
    }

    setIsRecalculating(true);

    try {
      const response = await recalculateMembershipBalance(token, effectiveMembershipId);

      if (response?.success) {
        // Update membership with new balance
        dispatch(setMembership({
          ...membership,
          balance: response.data.newBalance
        }));

        // Refresh invoices and future invoices
        await fetch();
        await fetchMembershipDetails();
        await fetchFutureInvoicesTotal();

        // Show success toast with balance change details
        const difference = response.data.difference;
        const changeText = difference !== 0
          ? ` (Changed by: ${formatCurrency(Math.abs(difference))})`
          : "";

        toast.success(
          `Balance recalculated successfully! New balance: ${formatCurrency(response.data.newBalance)}${changeText}`
        );
      } else {
        toast.error(response?.message || "Failed to recalculate balance");
      }
    } catch (error: unknown) {
      // Axios interceptor handles Sentry capture
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(
        err?.response?.data?.message || "Failed to recalculate balance. Please try again."
      );
    } finally {
      setIsRecalculating(false);
    }
  };

  // ===== REORDER MODE FUNCTIONS =====

  // Recalculate running balances for preview (frontend only)
  const recalculateBalancesPreview = (invoicesToRecalculate: BillingInvoice[]): BillingInvoice[] => {
    const currentBalance = membership?.balance || 0;
    const result = [...invoicesToRecalculate];

    // Work backwards from the end (oldest invoice in display order)
    // The last item in the array is the oldest, and its resulting balance should match current membership balance
    let runningBalance = currentBalance;

    // Process from newest to oldest (array index 0 to end)
    for (let i = 0; i < result.length; i++) {
      const inv = result[i];
      const isCredit = inv?.metadata?.credit || inv?.metadata?.type === "credit" || inv?.metadata?.display_payment_method;
      const amount = isCredit
        ? (inv?.metadata?.credit || inv?.amount || 0)
        : (inv?.metadata?.debit || inv?.amount || 0);

      // Calculate what previous_balance should be for this invoice
      // After this invoice is applied, balance changes to runningBalance
      // So previous_balance = runningBalance +/- amount (reverse the effect)
      let prevBalance: number;
      if (isCredit) {
        // Credit adds to balance, so previous was lower
        prevBalance = runningBalance - amount;
      } else {
        // Debit subtracts from balance, so previous was higher
        prevBalance = runningBalance + amount;
      }

      result[i] = {
        ...inv,
        metadata: {
          ...inv.metadata,
          previous_balance: prevBalance,
        },
      };

      // Move to the previous state (what balance was before this invoice)
      runningBalance = prevBalance;
    }

    return result;
  };

  // Enter reorder mode
  const enterReorderMode = () => {
    // Store original order and set reordered list to current filtered invoices
    const currentInvoices = filteredInvoices || [];
    setOriginalInvoices([...currentInvoices]);
    setReorderedInvoices([...currentInvoices]);
    setHasUnsavedReorderChanges(false);
    setIsReorderMode(true);
  };

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setReorderedInvoices((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(items, oldIndex, newIndex);
      // Recalculate balances for preview
      const withBalances = recalculateBalancesPreview(newOrder);

      setHasUnsavedReorderChanges(true);
      return withBalances;
    });
  };

  // Save reorder to backend
  const saveReorder = async () => {
    if (!effectiveMembershipId || reorderedInvoices.length === 0) return;

    setIsSavingReorder(true);
    try {
      const invoiceIds = reorderedInvoices.map((inv) => inv.id);
      const response = await reorderBillingHistory(token, effectiveMembershipId, invoiceIds);

      if (response?.success) {
        toast.success("Billing history order saved successfully!");
        // Refresh the data
        await fetch();
        await fetchMembershipDetails();
        setIsReorderMode(false);
        setReorderedInvoices([]);
        setOriginalInvoices([]);
        setHasUnsavedReorderChanges(false);
      } else {
        toast.error(response?.message || "Failed to save order");
      }
    } catch (error: unknown) {
      // Axios interceptor handles Sentry capture
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to save order. Please try again.");
    } finally {
      setIsSavingReorder(false);
    }
  };

  // Cancel reorder
  const cancelReorder = () => {
    setReorderedInvoices([]);
    setOriginalInvoices([]);
    setHasUnsavedReorderChanges(false);
    setIsReorderMode(false);
  };

  const handleExportCSV = () => {
    const headers = ["Description", "Credit", "Debit", "Balance", "Date"];
    const rows = invoicesWithBalance.map((invoice: BillingInvoice) => {
      // Description
      let description: string;
      if (invoice?.metadata?.display_payment_method) {
        const method = translatePaymentMethod(invoice.metadata.display_payment_method);
        const desc = translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment');
        description = `${method} - ${desc}`;
      } else {
        const desc = formatInvoiceLineItems(invoice) || translateDescription(invoice.description) || translateInvoiceType(invoice.metadata?.display_invoice_type);
        description = `${t('membership:billing.invoice')} - ${desc}`;
      }

      // Credit (raw number so spreadsheets treat as numeric)
      const creditVal = invoice?.metadata?.credit ? Number(invoice.metadata.credit).toFixed(2) : "0.00";

      // Debit
      const isCredit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
      let debitVal: string;
      if (invoice?.metadata?.debit) {
        debitVal = Number(invoice.metadata.debit).toFixed(2);
      } else if (!isCredit && invoice?.amount) {
        debitVal = Number(invoice.amount).toFixed(2);
      } else {
        debitVal = "0.00";
      }

      // Balance (same logic as SortableTableRow lines 234-267)
      let balanceVal = "";
      if (invoice?.metadata?.previous_balance !== undefined) {
        const prev = Number(invoice.metadata.previous_balance) || 0;
        const credit =
          invoice.metadata.credit !== undefined
            ? Number(invoice.metadata.credit)
            : invoice.payments
                ?.filter((p: InvoicePayment) => p.type === "credit")
                ?.reduce((sum: number, p: InvoicePayment) => sum + Number(p.amount), 0) ?? 0;
        const debit =
          invoice.metadata.debit !== undefined
            ? Number(invoice.metadata.debit)
            : invoice?.status === "outstanding" && invoice?.amount
            ? Number(invoice.amount)
            : invoice.payments
                ?.filter((p: InvoicePayment) => p.type === "debit")
                ?.reduce((sum: number, p: InvoicePayment) => sum + Number(p.amount), 0) ?? 0;
        let remaining = prev;
        if (invoice.metadata.type === "credit") {
          remaining += credit;
        } else if (invoice.metadata.type === "debit" || (invoice?.status === "outstanding" && invoice?.amount)) {
          remaining -= debit;
        }
        balanceVal = remaining.toFixed(2);
      }

      // Date
      const dateVal = formatDateOnly(invoice?.due_date);

      return [description, creditVal, debitVal, balanceVal, dateVal];
    });

    exportCsv(
      `billing_history_${membership?.first_name}_${membership?.last_name}_${localTodayYmd()}.csv`,
      rows.map((row: string[]) => Object.fromEntries(headers.map((h, i) => [h, row[i]])))
    );
  };

  useEffect(() => {}, [selectedInvoice]);

  // Update invoice
  const handleEditInvoiceSubmit = async (updatedInvoice: BillingInvoice) => {
    setIsloading(true);
    try {
      const payload = {
        invoiceId: updatedInvoice?.id,
        amount: updatedInvoice?.amount,
        description: updatedInvoice?.description,
        dueDate: updatedInvoice?.due_date,
        status: updatedInvoice?.status,
        tenantId: updatedInvoice?.tenant_id,
        metadata: updatedInvoice?.metadata,
        // The server refuses an amount change unless the editor says it was
        // deliberate (a note or date edit used to erase charges to $0).
        amountChanged: (updatedInvoice as any)?.amountChanged === true,
      };
      const res = await updateBillingHistoryInvoice(payload, token);

      if (res?.status === 200) {
        toast.success(res?.data?.message || "Invoice updated");
        setIsEditModalOpen(false);
        setIsloading(false);
        // Refresh data in background
        fetch().catch(() => {});
        fetchMembershipDetails().catch(() => {});
        setRefreshApiCalls(prev => prev + 1);
      } else {
        toast.error("Failed to update invoice. Please try again.");
      }
    } catch (err: unknown) {
      // The server's own words ("This edit would change the amount from
      // $90.55 to $0.00 ..."), not axios's "Request failed with status 409".
      toast.error(getPaymentErrorMessage(err, "The invoice was not updated. Please try again."));
    } finally {
      setIsloading(false);
      setIsEditModalOpen(false);
    }
  };
  const handelFetchMemberData = () => {
    fetch();
    fetchMembershipDetails();
  };

  // Resolve the linked registration charge for the payment being refunded so
  // RefundModal can offer the "also void the related charge" option. Camp /
  // clinic registration PAYMENT invoices carry metadata.paidForInvoiceId →
  // the charge invoice, which is already in our loaded `invoices` list.
  const refundLinkedCharge = useMemo(() => {
    const chargeId = (refundInvoice?.metadata as any)?.paidForInvoiceId;
    if (!chargeId) return null;
    return invoices?.find((inv) => inv.id === chargeId) || null;
  }, [refundInvoice, invoices]);

  // Reverse-sync: if the linked charge is for a camp/clinic/event registration,
  // surface the registration so RefundModal can offer to cancel it too (the
  // void zeroes the billing; the roster still shows the member until cancelled).
  const refundLinkedRegistration = useMemo(() => {
    const m: any = refundLinkedCharge?.metadata || {};
    const registrationId = m.registration_id;
    if (!registrationId) return null;
    let entityType: "camp" | "clinic" | "event" | null = null;
    let entityId: string | null = null;
    if (m.camp_id) { entityType = "camp"; entityId = m.camp_id; }
    else if (m.clinic_id) { entityType = "clinic"; entityId = m.clinic_id; }
    else if (m.event_id) { entityType = "event"; entityId = m.event_id; }
    if (!entityType || !entityId) return null;
    return { entityType, entityId, registrationId, entityName: m[`${entityType}_name`] || "" };
  }, [refundLinkedCharge]);

  // Get current items for pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  // ✅ Filter out future invoices (due tomorrow or later). due_date is the
  // tenant's calendar date serialized as a NAIVE timestamp
  // (`2026-09-01T00:00:00`, no zone) — compare date parts. Parsing it as UTC
  // and comparing against a local-midnight Date shifted every invoice by the
  // UTC offset and put tomorrow's dues in this table (2026-08-31).
  const todayYmd = localTodayYmd();
  const filteredInvoices = invoices?.filter(
    (invoice: BillingInvoice) => !isFutureInvoice(invoice.due_date, todayYmd)
  );

  // Calculate running balance for each invoice if not already in metadata
  // When in reorder mode, use reorderedInvoices (which already have recalculated balances)
  const invoicesWithBalance = useMemo(() => {
    // In reorder mode, use reorderedInvoices directly
    if (isReorderMode && reorderedInvoices.length > 0) {
      return reorderedInvoices;
    }

    // Normal mode: use backend-calculated balances, falling back to metadata or local calculation
    return filteredInvoices?.map((invoice: BillingInvoice, index: number) => {
      // Prefer backend-calculated running balance (calculatedPreviousBalance)
      // over stale metadata.previous_balance from invoice creation time
      if (invoice?.calculatedPreviousBalance !== undefined) {
        return {
          ...invoice,
          metadata: {
            ...invoice.metadata,
            previous_balance: invoice.calculatedPreviousBalance,
          },
        };
      }

      // If previous_balance is already set in metadata, use it
      if (invoice?.metadata?.previous_balance !== undefined) {
        return invoice;
      }

      // Last resort: calculate running balance based on previous invoices
      let runningBalance = membership?.balance || 0;

      // Go through all previous invoices (in reverse chronological order)
      for (let i = index + 1; i < filteredInvoices.length; i++) {
        const prevInvoice = filteredInvoices[i];
        const isCredit = prevInvoice?.metadata?.credit || prevInvoice?.metadata?.type === "credit" || prevInvoice?.metadata?.display_payment_method;
        const creditAmount = prevInvoice?.metadata?.credit || 0;
        const debitAmount = prevInvoice?.metadata?.debit || (prevInvoice?.status === "outstanding" ? prevInvoice?.amount : 0);

        if (isCredit) {
          runningBalance -= creditAmount;
        } else {
          runningBalance += debitAmount;
        }
      }

      // Set the previous balance for this invoice
      return {
        ...invoice,
        metadata: {
          ...invoice.metadata,
          previous_balance: runningBalance
        }
      };
    }) || [];
  }, [filteredInvoices, isReorderMode, reorderedInvoices, membership?.balance]);

  const currentItems = invoicesWithBalance?.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  // Unpaid charges the current page does not show (see billingPaging).
  const unpaidOffPage = unpaidChargesOffPage(
    invoicesWithBalance,
    indexOfFirstItem,
    indexOfLastItem,
    membership?.balance
  );


  // useEffect
  useEffect(() => {
    // Fetch membership details first
    const fetchMembershipDetailsFromToken = async () => {
      try {
        const response = await getMembership(token);

        // Assuming the membership details have a field `id`
        if (response && response.data) {
          dispatch(setMembership(response?.data?.data));
        }
      } catch (err) {
        // Axios interceptor handles Sentry capture
      }
    };

    // Only fetch if we have a membership ID
    if (effectiveMembershipId) {
      fetch();
    }

    // Fetch member when on Membership Dashboard (not when using prop membershipId)
    if (source !== "EditMembershipPage" && !propMembershipId) {
      fetchMembershipDetailsFromToken();
    }
  }, [refreshApiCalls, isCreatedManualInvoice, effectiveMembershipId]);

  // Reload when the user comes back to the tab OR the window. This is the
  // page's answer to "someone else changed this account": there is no live
  // channel (see refreshBilling above). `visibilitychange` covers switching
  // tabs and unlocking a phone; `focus` covers switching windows, which does
  // not change visibility. It used to reload the history only, so the future
  // invoices and scheduled payments totals stayed stale.
  // Also poll the balance every 60s while the billing page is visible.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && effectiveMembershipId) {
        refreshBilling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    // Poll every 60 seconds while visible
    const pollInterval = setInterval(() => {
      if (document.visibilityState === "visible" && effectiveMembershipId) {
        fetchMembershipDetails();
      }
    }, 60000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
      clearInterval(pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMembershipId, refreshBilling]);

  if (loading) {
    return (
      <div
        className={`w-full flex justify-center items-center ${
          isMobileDevice ? "p-4" : ""
        }`}
        style={{ height: isMobileDevice ? "auto" : "calc(100vh - 110px)" }}
      >
        <div
          className={`flex justify-center items-center ${
            isMobileDevice ? "mb-2" : "mb-4"
          }`}
        >
          <div
            className={`border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin ${
              isMobileDevice ? "w-5 h-5" : "w-6 h-6"
            }`}
          ></div>
          <span className={`ml-2 text-gray-500 dark:text-gray-400 ${isMobileDevice ? "text-sm" : "text-base"}`}>
            {t('membership:billing.loading')}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>{t('common:messages.error')}: {error}</div>;
  }

  // Wrapper for view-billing page
  const isViewBillingPage =
    source !== "dashboard" && source !== "EditMembershipPage";

  // Billing tabs apply only on EditMembershipPage; every other context
  // (member self-view) renders Billing History + Payment Settings stacked.
  // Tabbed Billing History | Payment Settings — staff Edit Membership AND the
  // member's own view-billing page (rendered with no `source`). NOT the dashboard
  // widget or the staff invoice-edit embeds (which pass explicit source strings).
  const billingTabsActive = source === "EditMembershipPage" || !source;
  const showHistory = !billingTabsActive || activeBillingTab === "history";
  const showPayment = !billingTabsActive || activeBillingTab === "payment";

  const billingContent = (
    <>
      {/* Multi-family switcher — renders only when the caller has access
          to >1 membership. Hidden inside EditMembershipPage where staff
          is already explicitly viewing one member's record. */}
      {source !== "EditMembershipPage" && <MembershipSwitcher />}
      {/* Balance — BalanceDisplay only in non-tabbed contexts (dashboard /
          invoice embeds). Staff + member tabbed views use the unified Account
          Balance card inside the Billing History tab below. */}
      {!billingTabsActive ? (
        <div className={`${isMobileDevice ? "mb-4" : "mb-6"}`}>
          <BalanceDisplay
            balance={membership?.balance || 0}
            futureInvoicesTotal={futureInvoicesTotal}
            widthClass={
              isMobileDevice
                ? "w-full"
                : isViewBillingPage
                ? "w-full"
                : source === "dashboard"
                ? widthClass
                : "md:w-1/3 w-full"
            }
            showPaymentLink={isMember}
            onPaymentSuccess={() => {
              setRefreshApiCalls(prev => prev + 1);
              // Refetch membership to get updated balance
              fetchMembershipDetails();
            }}
          />
        </div>
      ) : (
        <></>
      )}

      {/* Active enrollments strip — "what are these dues FOR?"
          context so staff can verify program + schedule + duration
          without switching tabs. Renders only when there's at least
          one active enrollment. On EditMembershipPage this is merged into the
          Account Balance card inside the Billing History tab instead. */}
      {activeEnrollments.length > 0 && !billingTabsActive && (
        <div
          className={`${getCardBackground(isDarkMode)} rounded-md p-3 mb-3 border ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div
            className={`text-xs font-medium uppercase tracking-wide mb-2 ${getSecondaryText(isDarkMode)}`}
          >
            Active enrollments — what dues are billing for
          </div>
          <ul className="space-y-1">
            {activeEnrollments.map((e) => (
              <li
                key={e.enrollment_id}
                className={`text-sm ${getPrimaryText(isDarkMode)} flex flex-wrap items-baseline gap-x-2`}
              >
                <span className="font-medium">{e.athlete_name}</span>
                <span className={getSecondaryText(isDarkMode)}>—</span>
                <span>{e.program_label}</span>
                {e.schedule_label && (
                  <>
                    <span className={getSecondaryText(isDarkMode)}>·</span>
                    <span className={getSecondaryText(isDarkMode)}>
                      {e.schedule_label}
                    </span>
                  </>
                )}
                {e.duration_hours != null && (
                  <>
                    <span className={getSecondaryText(isDarkMode)}>·</span>
                    <span className={getSecondaryText(isDarkMode)}>
                      {e.duration_hours === 1
                        ? "1 hour"
                        : `${e.duration_hours} hours`}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Billing History — when tabbed (staff Edit Membership + member view),
          envelope the whole tab area in a bordered panel that connects to the
          tab strip, matching the other tab groups. */}
      <div
        className={`${getCardBackground(isDarkMode)} ${
          billingTabsActive
            ? `rounded-lg border ${getLightBorder(isDarkMode)} p-3 sm:p-4`
            : ""
        }`}
      >
        {fetchLoading && (
          <div className="flex justify-between">
            <div></div>
            <div
              className={`flex items-center space-x-4 mb-4 ${getMutedText(
                isDarkMode
              )}`}
            >
              <div
                className={`w-4 h-4 border-2 ${getLightBorder(
                  isDarkMode
                )} border-t-transparent rounded-full animate-spin`}
              ></div>
              <span className="text-sm">{t('membership:billing.updatingResults')}</span>
            </div>
          </div>
        )}
        {/* Account Balance card moved into the Billing History tab below
            (combined with the active-enrollments context). */}

        {/* Billing tabs — Billing History | Payment Settings (EditMembershipPage only) */}
        {billingTabsActive && !billingTab && (
          <div className={`flex gap-1 border-b ${getLightBorder(isDarkMode)} mb-4`}>
            {[
              { id: "history" as const, label: "Billing History" },
              { id: "payment" as const, label: "Payment Settings" },
            ].map((tb) => (
              <button
                key={tb.id}
                type="button"
                onClick={() => setActiveBillingTab(tb.id)}
                className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
                  activeBillingTab === tb.id
                    ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                    : `border-transparent ${getMutedText(isDarkMode)} hover:${getSecondaryText(isDarkMode)}`
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        )}

        {/* Billing History tab content */}
        {showHistory && (
        <>
        {/* Account Balance + "what dues are billing for" — one combined card,
            inside the Billing History tab. Shown for staff (Edit Membership)
            AND the member's own view-billing page. */}
        {billingTabsActive && (
          <div
            className={`w-full p-4 ${getPageBackground(isDarkMode)} rounded-lg mb-6`}
          >
            <div className="flex items-center justify-between">
              <h3 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
                {t('membership:billing.accountBalance')}
              </h3>
              <div className="flex items-center gap-2">
                <span
                  className={`font-bold ${
                    (membership?.balance || 0) >= 0 ? "text-green-600" : "text-red-600"
                  } ${isMobileDevice ? "text-xl" : "text-2xl"}`}
                >
                  {formatCurrency(membership?.balance ?? 0)}
                </span>
                {canManageBilling && (
                  <Tooltip content="Recalculate balance from all invoices">
                    <button
                      onClick={handleRecalculateBalance}
                      disabled={isRecalculating}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label="Recalculate account balance"
                      aria-busy={isRecalculating}
                    >
                      <RefreshCw
                        size={18}
                        className={`${
                          isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                        } transition ${isRecalculating ? "animate-spin" : ""}`}
                      />
                    </button>
                  </Tooltip>
                )}
                {/* Member self-pay — same PaymentModal flow as the old
                    BalanceDisplay card. */}
                {isMember && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowMemberPayModal(true)}
                    icon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    }
                  >
                    {t('membership:billing.makeAPayment', { defaultValue: 'Make a Payment' })}
                  </Button>
                )}
              </div>
            </div>
            {isMember && (
              <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
                {/* $0 is settled, not a credit (2026-10-11). */}
                {t(`membership:billing.${balanceStatusKey(membership?.balance)}`, {
                  defaultValue: BALANCE_STATUS_DEFAULTS[balanceStatusKey(membership?.balance)],
                })}
              </p>
            )}
            {(futureInvoicesTotal > 0 || scheduledPaymentsTotal > 0) && (
              <p className={`text-sm ${getMutedText(isDarkMode)} mt-2 text-right`}>
                Upcoming:
                {futureInvoicesTotal > 0 && (
                  <span className={`font-semibold ${isDarkMode ? "text-orange-400" : "text-orange-600"}`}> -{formatCurrency(futureInvoicesTotal)}</span>
                )}
                {scheduledPaymentsTotal > 0 && (
                  <span className={`font-semibold ${isDarkMode ? "text-green-400" : "text-green-600"}`}> +{formatCurrency(scheduledPaymentsTotal)}</span>
                )}
              </p>
            )}
            {/* Drafts are the club's unsent invoices: staff see them counted
                apart; a family never does (2026-10-11). */}
            {((!isMember && upcomingDrafts.count > 0) || failedScheduled.count > 0) && (
              <p data-testid="upcoming-apart" className={`text-xs ${getMutedText(isDarkMode)} mt-1 text-right`}>
                {!isMember && upcomingDrafts.count > 0 && (
                  <span>
                    {upcomingDrafts.count} draft{upcomingDrafts.count === 1 ? "" : "s"} not sent yet ({formatCurrency(upcomingDrafts.total)})
                  </span>
                )}
                {!isMember && upcomingDrafts.count > 0 && failedScheduled.count > 0 && <span> · </span>}
                {failedScheduled.count > 0 && (
                  <span className={isDarkMode ? "text-red-400" : "text-red-600"}>
                    {failedScheduled.count} scheduled payment{failedScheduled.count === 1 ? "" : "s"} failed ({formatCurrency(failedScheduled.total)}), not counted
                  </span>
                )}
              </p>
            )}
            {activeEnrollments.length > 0 && (
              <div className={`mt-3 pt-3 border-t ${getLightBorder(isDarkMode)}`}>
                <div className={`text-xs font-medium uppercase tracking-wide mb-2 ${getSecondaryText(isDarkMode)}`}>
                  Active enrollments — what dues are billing for
                </div>
                <ul className="space-y-1">
                  {activeEnrollments.map((e) => (
                    <li key={e.enrollment_id} className={`text-sm ${getPrimaryText(isDarkMode)} flex flex-wrap items-baseline gap-x-2`}>
                      <span className="font-medium">{e.athlete_name}</span>
                      <span className={getSecondaryText(isDarkMode)}>—</span>
                      <span>{e.program_label}</span>
                      {e.schedule_label && (
                        <>
                          <span className={getSecondaryText(isDarkMode)}>·</span>
                          <span className={getSecondaryText(isDarkMode)}>{e.schedule_label}</span>
                        </>
                      )}
                      {e.duration_hours != null && (
                        <>
                          <span className={getSecondaryText(isDarkMode)}>·</span>
                          <span className={getSecondaryText(isDarkMode)}>
                            {e.duration_hours === 1 ? "1 hour" : `${e.duration_hours} hours`}
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div
          className={`w-full flex ${
            isMobileDevice
              ? "flex-col gap-4 mb-4"
              : "items-end justify-between mb-6"
          }`}
        >
          {/* Title */}
          <h2
            className={`font-bold ${getPrimaryText(isDarkMode)} flex-shrink-0 ${
              isMobileDevice
                ? isMobileLandscape
                  ? "text-lg"
                  : "text-xl"
                : "text-2xl"
            }`}
          >
            {t('membership:billing.title')}
          </h2>

          {/* Create Invoice Button + Annual Registration Month */}
          {source === "EditMembershipPage" && (
            <div
              className={`flex gap-4 ${
                isMobileDevice ? "w-full flex-col items-stretch" : "items-end"
              }`}
            >
              {/* Reorder Mode Controls */}
              {isReorderMode ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    fullWidth={isMobileDevice}
                    onClick={cancelReorder}
                    disabled={isSavingReorder}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="save"
                    size="md"
                    fullWidth={isMobileDevice}
                    onClick={saveReorder}
                    disabled={!hasUnsavedReorderChanges || isSavingReorder}
                  >
                    {isSavingReorder ? "Saving..." : "Save Order"}
                  </Button>
                </>
              ) : (
                <>
                  {/* Secondary actions (Export CSV, Reorder) tucked behind an
                      overflow menu so the header keeps just its primary
                      actions. */}
                  {(() => {
                    const opts = [
                      (isAdmin || isCentralOwner) && invoicesWithBalance?.length > 0
                        ? { label: "Export CSV", icon: <Download size={16} />, onClick: handleExportCSV }
                        : null,
                      canReorder && invoices?.length > 1
                        ? { label: "Reorder", icon: <GripVertical size={16} />, onClick: enterReorderMode }
                        : null,
                    ].filter(Boolean) as { label: string; icon: React.ReactNode; onClick: () => void }[];
                    return opts.length ? (
                      <ActionMenu iconOnly items={opts} ariaLabel="Billing options" size="sm" />
                    ) : null;
                  })()}
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    fullWidth={isMobileDevice}
                    onClick={() => setShowPaymentModal(true)}
                  >
                    {t('membership:billing.acceptPayments')}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    fullWidth={isMobileDevice}
                    onClick={() => setShowCreateInvoiceModal(true)}
                  >
                    {t('membership:billing.createInvoice')}
                  </Button>
                </>
              )}

              <div className={`${isMobileDevice ? "w-full" : ""}`}>
                <Select
                  label={t('membership:billing.annualRegistrationMonth')}
                  name="annual_registration_month"
                  size={isMobileDevice ? "md16" : "md"}
                  value={membership?.annual_registration_month ?? ""}
                  onChange={handleAnnualMonthChange}
                  className="cursor-pointer"
                  options={[
                    { value: "", label: t('membership:billing.selectMonth') },
                    ...[
                      "January",
                      "February",
                      "March",
                      "April",
                      "May",
                      "June",
                      "July",
                      "August",
                      "September",
                      "October",
                      "November",
                      "December",
                    ].map((month) => ({ value: month, label: month })),
                  ]}
                />
              </div>
            </div>
          )}
        </div>

        {/* Critical billing highlight — admin/staff only, right by the
            Accept Payment / Create Invoice actions so any per-member caveat
            is visible at the moment those actions are taken. Single note,
            saved to memberships.billing_highlight_note on blur. Label is
            intentionally omitted; the placeholder carries the intent. */}
        {source === "EditMembershipPage" && canManageBilling && (
          <div className="w-full mb-4">
            <div className="relative">
              <Input
                type="text"
                value={highlightNote}
                onChange={(e) => setHighlightNote(e.target.value)}
                onBlur={handleHighlightNoteBlur}
                maxLength={280}
                aria-label="Critical billing highlight note"
                placeholder="Critical billing note shown to staff here — e.g. 'Paying by check, do not auto-charge' · 'Past due, hold new invoices' · 'Billing questions to owner only'"
                className={`pr-20 ${
                  highlightNote.trim() ? "ring-2 ring-amber-400 font-medium" : ""
                }`}
              />
              {savingHighlight && (
                <span
                  className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs ${getMutedText(
                    isDarkMode
                  )}`}
                >
                  Saving…
                </span>
              )}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="w-full flex justify-between">
          <div
            className={`flex items-center space-x-4 ${
              isMobileDevice ? "mb-3" : "mb-4"
            }`}
          >
            <div className="flex items-center space-x-2">
              <div
                className={`bg-green-100 border border-green-400 rounded ${
                  isMobileDevice ? "w-3 h-3" : "w-4 h-4"
                }`}
              />
              <span
                className={`${getSecondaryText(isDarkMode)} ${
                  isMobileDevice
                    ? isMobileLandscape
                      ? "text-xs"
                      : "text-sm"
                    : "text-sm"
                }`}
              >
                {t('membership:billing.credit')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div
                className={`bg-red-200 border border-red-400 rounded ${
                  isMobileDevice ? "w-3 h-3" : "w-4 h-4"
                }`}
              />
              <span
                className={`${getSecondaryText(isDarkMode)} ${
                  isMobileDevice
                    ? isMobileLandscape
                      ? "text-xs"
                      : "text-sm"
                    : "text-sm"
                }`}
              >
                {t('membership:billing.debit')}
              </span>
            </div>
          </div>
          {fetchLoading && (
            <div
              className={`flex items-center space-x-4 mb-4 ${getMutedText(
                isDarkMode
              )}`}
            >
              <div
                className={`w-4 h-4 border-2 ${getLightBorder(
                  isDarkMode
                )} border-t-transparent rounded-full animate-spin`}
              ></div>
              <span className="text-sm">{t('membership:billing.updatingResults')}</span>
            </div>
          )}
        </div>

        {invoices?.length > 0 ? (
          <div>
            {/* Desktop Table */}
            {!isMobileDevice && (
              <div
                data-testid="billing-history-scroll"
                className={`relative overflow-x-auto rounded-lg border ${getLightBorder(isDarkMode)} ${
                  isDarkMode ? "[color-scheme:dark] [scrollbar-color:#4b5563_#1f2937]" : ""
                }`}
              >
                <Table
                  compact
                  scrollable={false}
                  className={`3xl:text-base 4xl:text-lg ${getMutedText(isDarkMode)}`}
                >
                  <THead>
                    <tr>
                      {/* Drag Handle Column Header - Only in reorder mode */}
                      {isReorderMode && (
                        <TH scope="col" className="w-10"></TH>
                      )}
                      <TH
                        scope="col"
                      >
                        {t('common:labels.description')}
                      </TH>
                      <TH
                        scope="col"
                        className="whitespace-nowrap"
                      >
                        {t('membership:billing.creditHeader')}
                      </TH>
                      <TH
                        scope="col"
                        className="whitespace-nowrap"
                      >
                        {t('membership:billing.debitHeader')}
                      </TH>
                      <TH
                        scope="col"
                        className="whitespace-nowrap"
                      >
                        {t('membership:billing.balance')}
                      </TH>
                      <TH
                        scope="col"
                        className="whitespace-nowrap"
                      >
                        {t('common:labels.date')}
                      </TH>
                      <TH
                        scope="col"
                        className="hidden"
                      >
                        {t('common:labels.actions')}
                      </TH>
                    </tr>
                  </THead>

                  {isReorderMode ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={invoicesWithBalance.map((inv: BillingInvoice) => inv.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <tbody>
                          {invoicesWithBalance?.map((invoice: BillingInvoice) => {
                            const isCredit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                            const isDebit = invoice?.metadata?.debit || invoice?.status === "outstanding" || (!isCredit && invoice?.amount > 0);

                            return (
                              <SortableTableRow
                                key={invoice.id}
                                invoice={invoice}
                                isReorderMode={isReorderMode}
                                isDarkMode={isDarkMode}
                                isCredit={isCredit}
                                isDebit={isDebit}
                                getPrimaryText={getPrimaryText}
                                getPaymentMethodColor={getPaymentMethodColor}
                                translatePaymentMethod={translatePaymentMethod}
                                translateDescription={translateDescription}
                                translateInvoiceType={translateInvoiceType}
                                t={t}
                                onRowClick={(e, inv) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipData({
                                    x: rect.left + rect.width / 2,
                                    y: rect.bottom + window.scrollY,
                                    invoice: inv,
                                  });
                                }}
                              />
                            );
                          })}
                        </tbody>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <tbody>
                      {currentItems?.map((invoice: BillingInvoice) => {
                        // Determine if this is a credit (payment) or debit (invoice)
                        const isCredit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                        const isDebit = invoice?.metadata?.debit || invoice?.status === "outstanding" || (!isCredit && invoice?.amount > 0);

                        return (
                        <tr
                          key={invoice.id}
                          className={`group relative ${
                            isCredit
                              ? isDarkMode
                                ? "bg-green-900"
                                : "bg-green-100"
                              : isDebit
                              ? isDarkMode
                                ? "bg-red-900"
                                : "bg-red-200"
                              : isDarkMode
                              ? "bg-gray-800"
                              : "bg-white"
                          } border-b ${
                            isDarkMode ? "border-gray-700" : "border-gray-200"
                          } cursor-pointer`}
                          onClick={(e) => {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setTooltipData({
                              x: rect.left + rect.width / 2,
                              y: rect.bottom + window.scrollY,
                              invoice,
                            });
                          }}
                        >
                          {/* Description — capped width so very long
                              line-item descriptions (camp / clinic
                              registration with full session detail)
                              don't push Credit/Debit/Balance/Date off
                              the right edge on smaller monitors. Full
                              text remains accessible via the title
                              attribute on hover. Reported by Chris @ GoY
                              2026-05-10. */}
                          <TD
                            className={`max-w-md ${getPrimaryText(
                              isDarkMode
                            )}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {/* Payment Method Tag (for payments) */}
                              {invoice?.metadata?.display_payment_method && (
                                <>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getPaymentMethodColor(invoice.metadata.display_payment_method)}`}>
                                    {translatePaymentMethod(invoice.metadata.display_payment_method)}
                                  </span>
                                  <span className="truncate min-w-0" title={translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}>
                                    {translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}
                                  </span>
                                </>
                              )}
                              {/* Invoice Badge and Description (for invoices) */}
                              {!invoice?.metadata?.display_payment_method && (
                                <>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                    {t('membership:billing.invoice')}
                                  </span>
                                  <ChargeStatusBadge invoice={invoice} isDarkMode={isDarkMode} />
                                  <div className="min-w-0 flex-1">
                                    <InvoiceRowDescription
                                      invoice={invoice}
                                      isDarkMode={isDarkMode}
                                      translateDescription={translateDescription}
                                      translateInvoiceType={translateInvoiceType}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </TD>

                          {/* Credit */}
                          <TD
                            className={`${
                              isDarkMode ? "text-green-400" : "text-green-600"
                            }`}
                          >
                            {invoice?.metadata?.credit ? (
                              <div>+{formatCurrency(invoice.metadata.credit)}</div>
                            ) : (
                              <>&nbsp; ${"0.00"}</>
                            )}
                          </TD>

                          {/* Debit */}
                          <TD
                            className={`${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            {invoice?.metadata?.debit ? (
                              <div>
                                -{formatCurrency(Number(invoice?.metadata?.debit))}
                              </div>
                            ) : !isCredit && invoice?.amount ? (
                              <div>-{formatCurrency(Number(invoice?.amount))}</div>
                            ) : (
                              <>${"0.00"}</>
                            )}
                          </TD>

                          {/* Amount */}
                          <TD
                            className={`font-medium ${getPrimaryText(
                              isDarkMode
                            )}`}
                          >
                            {invoice?.metadata?.previous_balance !== undefined ? (
                              (() => {
                                const prev =
                                  Number(invoice.metadata.previous_balance) || 0;
                                const credit =
                                  invoice.metadata.credit !== undefined
                                    ? Number(invoice.metadata.credit)
                                    : invoice.payments
                                        ?.filter((p: InvoicePayment) => p.type === "credit")
                                        ?.reduce(
                                          (sum: number, p: InvoicePayment) =>
                                            sum + Number(p.amount),
                                          0
                                        ) ?? 0;
                                const debit =
                                  invoice.metadata.debit !== undefined
                                    ? Number(invoice.metadata.debit)
                                    : invoice?.status === "outstanding" &&
                                      invoice?.amount
                                    ? Number(invoice.amount)
                                    : invoice.payments
                                        ?.filter((p: InvoicePayment) => p.type === "debit")
                                        ?.reduce(
                                          (sum: number, p: InvoicePayment) =>
                                            sum + Number(p.amount),
                                          0
                                        ) ?? 0;

                                let remaining = prev; // start with previous balance

                                if (invoice.metadata.type === "credit") {
                                  remaining += credit;
                                } else if (
                                  invoice.metadata.type === "debit" ||
                                  (invoice?.status === "outstanding" &&
                                    invoice?.amount)
                                ) {
                                  remaining -= debit;
                                }

                                return <span>{formatCurrency(remaining)}</span>;
                              })()
                            ) : (
                              <span>—</span>
                            )}
                          </TD>

                          {/* Date */}
                          <TD
                            className={`${getPrimaryText(
                              isDarkMode
                            )}`}
                          >
                            {formatDateOnly(invoice?.due_date)}
                          </TD>

                          <TD
                            className={`${getPrimaryText(
                              isDarkMode
                            )} hidden`}
                          >
                            <InvoiceActions
                              onEdit={() => handleEdit(invoice?.id)}
                              onDelete={() => handleDelete}
                              onAccept={() => {}}
                              isPaid={true}
                              invoice={invoice}
                              openMenuId={openMenuId}
                              setOpenMenuId={setOpenMenuId}
                            />
                          </TD>
                        </tr>
                        );
                      })}
                    </tbody>
                  )}
                </Table>

                {/* Tooltip div with backdrop */}
                {tooltipData && (
                  <>
                    {/* Backdrop to catch clicks outside */}
                    <div
                      className="fixed inset-0 z-40 bg-black bg-opacity-20"
                      onClick={() => setTooltipData(null)}
                    />
                    {/* Tooltip */}
                    <MemberInvoiceTooltip
                      invoice={tooltipData.invoice}
                      onClose={() => setTooltipData(null)}
                      isAdminOrStaff={canManageBilling}
                      {...peopleDetailProps(tooltipData.invoice)}
                      {...(canManageBilling && {
                        onEdit: (invoiceId) => {
                          setTooltipData(null);
                          handleEdit(invoiceId);
                        },
                        onDelete: (invoice) => {
                          setTooltipData(null);
                          setInvoiceToDelete(invoice);
                          setShowDeleteModal(true);
                        },
                      })}
                      {...(canManageBilling && canRefund && {
                        onRefund: (invoice: BillingInvoice) => {
                          setTooltipData(null);
                          setRefundInvoice(invoice);
                          setShowRefundModal(true);
                        }
                      })}
                    />
                  </>
                )}
              </div>
            )}

            {/* Mobile Card Layout */}
            {isMobileDevice && (
              <div
                className={`space-y-3 ${
                  isMobileLandscape && !isReorderMode ? "grid grid-cols-2 gap-3 space-y-0" : ""
                }`}
              >
                {isReorderMode ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={invoicesWithBalance.map((inv: BillingInvoice) => inv.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {invoicesWithBalance?.map((invoice: BillingInvoice) => {
                        const credit =
                          invoice?.metadata?.credit ??
                          invoice.payments
                            ?.filter((p: InvoicePayment) => p.type === "credit")
                            ?.reduce((sum: number, p: InvoicePayment) => sum + p.amount, 0) ??
                          0;

                        const isCreditForDebit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                        const debitAmount = invoice?.metadata?.debit
                          ? invoice?.metadata?.debit
                          : invoice?.metadata?.paidForInvoiceId
                          ? invoice.amount
                          : !isCreditForDebit && invoice?.amount
                          ? invoice.amount
                          : 0;

                        const balance =
                          invoice?.metadata?.previous_balance !== undefined
                            ? (() => {
                                const prev = Number(invoice.metadata.previous_balance) || 0;
                                const creditAmt =
                                  invoice.metadata.credit !== undefined
                                    ? Number(invoice.metadata.credit)
                                    : invoice.payments
                                        ?.filter((p: InvoicePayment) => p.type === "credit")
                                        ?.reduce((sum: number, p: InvoicePayment) => sum + Number(p.amount), 0) ?? 0;
                                const debitAmt =
                                  invoice.metadata.debit !== undefined
                                    ? Number(invoice.metadata.debit)
                                    : invoice?.status === "outstanding" && invoice?.amount
                                    ? Number(invoice.amount)
                                    : invoice.payments
                                        ?.filter((p: InvoicePayment) => p.type === "debit")
                                        ?.reduce((sum: number, p: InvoicePayment) => sum + Number(p.amount), 0) ?? 0;

                                let remaining = prev;
                                if (invoice.metadata.type === "credit") {
                                  remaining += creditAmt;
                                } else if (invoice.metadata.type === "debit" || (invoice?.status === "outstanding" && invoice?.amount)) {
                                  remaining -= debitAmt;
                                }
                                return remaining;
                              })()
                            : invoice?.membership?.balance ?? 0;

                        const isCredit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                        const isDebit = invoice?.metadata?.debit || invoice?.status === "outstanding" || (!isCredit && invoice?.amount > 0);

                        return (
                          <SortableMobileCard
                            key={invoice.id}
                            invoice={invoice}
                            isReorderMode={isReorderMode}
                            isDarkMode={isDarkMode}
                            isCredit={isCredit}
                            isDebit={isDebit}
                            isMobileLandscape={isMobileLandscape}
                            credit={credit}
                            debitAmount={debitAmount}
                            balance={balance}
                            getPrimaryText={getPrimaryText}
                            getSecondaryText={getSecondaryText}
                            getMutedText={getMutedText}
                            getPaymentMethodColor={getPaymentMethodColor}
                            translatePaymentMethod={translatePaymentMethod}
                            translateDescription={translateDescription}
                            translateInvoiceType={translateInvoiceType}
                            t={t}
                            onCardClick={(e, inv) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTooltipData({
                                x: rect.left + rect.width / 2,
                                y: rect.bottom + window.scrollY,
                                invoice: inv,
                              });
                            }}
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  currentItems?.map((invoice: BillingInvoice) => {
                    const credit =
                      invoice?.metadata?.credit ??
                      invoice.payments
                        ?.filter((p: InvoicePayment) => p.type === "credit")
                        ?.reduce((sum: number, p: InvoicePayment) => sum + p.amount, 0) ??
                      0;

                    // Determine if this is a credit (payment) or debit (invoice) for debit calculation
                    const isCreditForDebit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                    const debitAmount = invoice?.metadata?.debit
                      ? invoice?.metadata?.debit
                      : invoice?.metadata?.paidForInvoiceId
                      ? invoice.amount
                      : !isCreditForDebit && invoice?.amount
                      ? invoice.amount
                      : 0;

                    const balance =
                      invoice?.metadata?.previous_balance !== undefined
                        ? (() => {
                            const prev =
                              Number(invoice.metadata.previous_balance) || 0;
                            const creditAmt =
                              invoice.metadata.credit !== undefined
                                ? Number(invoice.metadata.credit)
                                : invoice.payments
                                    ?.filter((p: InvoicePayment) => p.type === "credit")
                                    ?.reduce(
                                      (sum: number, p: InvoicePayment) =>
                                        sum + Number(p.amount),
                                      0
                                    ) ?? 0;
                            const debitAmt =
                              invoice.metadata.debit !== undefined
                                ? Number(invoice.metadata.debit)
                                : invoice?.status === "outstanding" &&
                                  invoice?.amount
                                ? Number(invoice.amount)
                                : invoice.payments
                                    ?.filter((p: InvoicePayment) => p.type === "debit")
                                    ?.reduce(
                                      (sum: number, p: InvoicePayment) =>
                                        sum + Number(p.amount),
                                      0
                                    ) ?? 0;

                            let remaining = prev;
                            if (invoice.metadata.type === "credit") {
                              remaining += creditAmt;
                            } else if (
                              invoice.metadata.type === "debit" ||
                              (invoice?.status === "outstanding" &&
                                invoice?.amount)
                            ) {
                              remaining -= debitAmt;
                            }
                            return remaining;
                          })()
                        : invoice?.membership?.balance ?? 0;

                    // Determine if this is a credit (payment) or debit (invoice)
                    const isCredit = invoice?.metadata?.credit || invoice?.metadata?.type === "credit" || invoice?.metadata?.display_payment_method;
                    const isDebit = invoice?.metadata?.debit || invoice?.status === "outstanding" || (!isCredit && invoice?.amount > 0);

                    return (
                      <div
                        key={invoice.id}
                        className={`rounded-lg border-2 shadow-sm transition-all duration-200 touch-manipulation ${
                          isCredit
                            ? "bg-green-50 border-green-200"
                            : isDebit
                            ? "bg-red-50 border-red-200"
                            : "bg-white border-gray-200"
                        } ${isMobileLandscape ? "p-3 min-h-[140px]" : "p-4"} cursor-pointer`}
                        style={{
                          minHeight: isMobileLandscape ? "140px" : "auto",
                        }}
                        onClick={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setTooltipData({
                            x: rect.left + rect.width / 2,
                            y: rect.bottom + window.scrollY,
                            invoice,
                          });
                        }}
                      >
                        {/* Header with Invoice ID and Amount */}
                        <div
                          className={`flex items-start justify-between ${
                            isMobileLandscape ? "mb-2" : "mb-3"
                          }`}
                        >
                          <div className="flex-1">
                            <div
                              className={`flex items-center space-x-2 ${
                                isMobileLandscape ? "mb-1" : "mb-2"
                              }`}
                            >
                              <div
                                className={`rounded-full flex items-center justify-center ${
                                  isCredit
                                    ? "bg-green-500"
                                    : isDebit
                                    ? "bg-red-500"
                                    : "bg-blue-500"
                                } ${isMobileLandscape ? "w-6 h-6" : "w-8 h-8"}`}
                              >
                                <svg
                                  className={`text-white ${
                                    isMobileLandscape ? "w-3 h-3" : "w-4 h-4"
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p
                                  className={`font-semibold ${getPrimaryText(
                                    isDarkMode
                                  )} ${
                                    isMobileLandscape ? "text-sm" : "text-base"
                                  }`}
                                >
                                  ID: {invoice?.id?.slice(0, 8)}...
                                </p>
                                <p
                                  className={`${getMutedText(isDarkMode)} ${
                                    isMobileLandscape ? "text-xs" : "text-sm"
                                  }`}
                                >
                                  {new Date(
                                    invoice?.due_date
                                  ).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div
                            className={`text-right ${
                              isMobileLandscape ? "ml-2" : "ml-4"
                            }`}
                          >
                            <p
                              className={`font-bold ${
                                isMobileLandscape ? "text-sm" : "text-lg"
                              } ${getPrimaryText(isDarkMode)}`}
                            >
                              {formatCurrency(balance)}
                            </p>
                            <p
                              className={`${getMutedText(isDarkMode)} ${
                                isMobileLandscape ? "text-xs" : "text-sm"
                              }`}
                            >
                              {t('membership:billing.balance')}
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        <div className={`${isMobileLandscape ? "mb-2" : "mb-3"}`}>
                          <div className="flex items-start gap-2 flex-wrap">
                            {/* Payment Method Tag (for payments) */}
                            {invoice?.metadata?.display_payment_method && (
                              <>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${getPaymentMethodColor(invoice.metadata.display_payment_method)}`}>
                                  {translatePaymentMethod(invoice.metadata.display_payment_method)}
                                </span>
                                <p
                                  className={`${getSecondaryText(
                                    isDarkMode
                                  )} leading-relaxed ${
                                    isMobileLandscape
                                      ? "text-sm line-clamp-2"
                                      : "text-base"
                                  }`}
                                >
                                  {translateDescription(invoice?.description) || t('membership:billing.invoiceTypes.payment')}
                                </p>
                              </>
                            )}
                            {/* Invoice Badge and Description (for invoices) */}
                            {!invoice?.metadata?.display_payment_method && (
                              <>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                  {t('membership:billing.invoice')}
                                </span>
                                <ChargeStatusBadge invoice={invoice} isDarkMode={isDarkMode} />
                                <div
                                  className={`${getSecondaryText(
                                    isDarkMode
                                  )} leading-relaxed ${
                                    isMobileLandscape
                                      ? "text-sm line-clamp-2"
                                      : "text-base"
                                  }`}
                                >
                                  <InvoiceRowDescription
                                    invoice={invoice}
                                    isDarkMode={isDarkMode}
                                    translateDescription={translateDescription}
                                    translateInvoiceType={translateInvoiceType}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Credit/Debit Row */}
                        <div
                          className={`grid grid-cols-2 gap-4 ${
                            isMobileLandscape ? "text-sm" : "text-base"
                          }`}
                        >
                          {/* Credit */}
                          <div
                            className={`flex items-center justify-between bg-green-100 border border-green-300 rounded-lg ${
                              isMobileLandscape ? "p-2" : "p-3"
                            }`}
                          >
                            <span className="text-green-800 font-medium">
                              {t('membership:billing.credit')}
                            </span>
                            <span className="text-green-700 font-bold">
                              {credit > 0 ? `+${formatCurrency(credit)}` : formatCurrency(0)}
                            </span>
                          </div>

                          {/* Debit */}
                          <div
                            className={`flex items-center justify-between bg-red-100 border border-red-300 rounded-lg ${
                              isMobileLandscape ? "p-2" : "p-3"
                            }`}
                          >
                            <span className="text-red-800 font-medium">
                              {t('membership:billing.debit')}
                            </span>
                            <span className="text-red-700 font-bold">
                              {debitAmount > 0
                                ? `-${formatCurrency(debitAmount)}`
                                : "$0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                {/* Tooltip div for mobile with backdrop */}
                {tooltipData && (
                  <>
                    {/* Backdrop to catch clicks/taps outside */}
                    <div
                      className="fixed inset-0 z-40 bg-black bg-opacity-20"
                      onClick={() => setTooltipData(null)}
                      onTouchEnd={() => setTooltipData(null)}
                    />
                    {/* Tooltip */}
                    <MemberInvoiceTooltip
                      invoice={tooltipData.invoice}
                      onClose={() => setTooltipData(null)}
                      isAdminOrStaff={canManageBilling}
                      {...peopleDetailProps(tooltipData.invoice)}
                      {...(canManageBilling && {
                        onEdit: (invoiceId) => {
                          setTooltipData(null);
                          handleEdit(invoiceId);
                        },
                        onDelete: (invoice) => {
                          setTooltipData(null);
                          setInvoiceToDelete(invoice);
                          setShowDeleteModal(true);
                        },
                      })}
                      {...(canManageBilling && canRefund && {
                        onRefund: (invoice: BillingInvoice) => {
                          setTooltipData(null);
                          setRefundInvoice(invoice);
                          setShowRefundModal(true);
                        }
                      })}
                    />
                  </>
                )}
              </div>
            )}

            {/* Pagination - Hidden in reorder mode since all items are shown.
                One line: range, rows-per-page, and arrow buttons. Page 1 is
                the NEWEST rows, so the left arrow goes to newer entries and
                the right arrow to older ones. */}
            {!isReorderMode && (
              <div className="mt-3 space-y-2">
                {/* A short page must not hide an unpaid invoice from staff
                    or the family: say how many are off this page and offer
                    to show them. */}
                {unpaidOffPage.count > 0 && (
                  <div
                    role="status"
                    data-testid="unpaid-off-page-notice"
                    className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm ${
                      isDarkMode
                        ? "border-amber-700 bg-amber-900/20 text-amber-200"
                        : "border-amber-300 bg-amber-50 text-amber-900"
                    }`}
                  >
                    <span>
                      {unpaidOffPage.count === 1
                        ? "1 unpaid invoice is not shown on this page."
                        : `${unpaidOffPage.count} unpaid invoices are not shown on this page.`}
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="xs"
                      onClick={() => {
                        setItemsPerPage(unpaidOffPage.rowsToShowAll);
                        setCurrentPage(1);
                      }}
                    >
                      Show unpaid
                    </Button>
                  </div>
                )}
                <BillingPager
                  idPrefix="billing-history"
                  total={invoicesWithBalance?.length || 0}
                  page={currentPage}
                  pageSize={itemsPerPage}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(size) => {
                    setItemsPerPage(size);
                    setCurrentPage(1);
                  }}
                  pageSizeOptions={pageSizeOptions(defaultHistoryPageSize(source), itemsPerPage)}
                  itemLabel="entries"
                  itemLabelSingular="entry"
                  previousLabel="Newer entries"
                  nextLabel="Older entries"
                />
              </div>
            )}
          </div>
        ) : (
          <div className={`text-center ${isMobileDevice ? "py-4" : "py-6"}`}>
            <div
              className={`mx-auto rounded-full ${getPageBackground(
                isDarkMode
              )} flex items-center justify-center ${
                isMobileDevice ? "w-10 h-10 mb-2" : "w-12 h-12 mb-3"
              }`}
            >
              <svg
                className={`${getMutedText(isDarkMode)} ${
                  isMobileDevice ? "w-5 h-5" : "w-6 h-6"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <p
              className={`${getMutedText(isDarkMode)} ${
                isMobileDevice
                  ? isMobileLandscape
                    ? "text-xs"
                    : "text-sm"
                  : "text-sm"
              }`}
            >
              {t('membership:billing.noHistory')}
            </p>
            <p
              className={`${getMutedText(isDarkMode)} mt-1 ${
                isMobileDevice
                  ? isMobileLandscape
                    ? "text-xs"
                    : "text-xs"
                  : "text-xs"
              }`}
            >
              {t('membership:billing.transactionsWillAppear')}
            </p>
          </div>
        )}
        </>
        )}
      {/* Future Invoices + Scheduled Payments — part of the Billing History tab */}
      {showHistory && (
        <>
          <MemberFutureInvoices
            refreshApiCalls={refreshApiCalls}
            setRefreshApiCalls={setRefreshApiCalls}
            source={source}
          />
          <MemberScheduledPayments
            refreshApiCalls={refreshApiCalls}
            setRefreshApiCalls={setRefreshApiCalls}
          />
        </>
      )}
      {/* The old "unpaid invoices" list used to be mounted here inside a
          hidden box (removed 2026-10-08). Nobody could see or press anything
          in it, but it still loaded the family's pending invoices, their
          payment history and their saved cards from Stripe on every visit
          and again after every action on this page. Nothing read what it
          loaded. */}

      {/* Payment Settings - Show for members viewing their own page, OR for admin/staff on EditMembershipPage.
          On EditMembershipPage it's the "Payment Settings" billing tab. */}
      {(isMember || (canManageBilling && source === "EditMembershipPage")) && showPayment && (
        <div className={billingTabsActive ? "" : "mt-6 sm:mt-8"}>
          <UpdateCardForm
            source={source || "view-billing"}
            membership={membership}
            canSendSetupEmail={canManageBilling && source === "EditMembershipPage"}
            staffActingForMember={canManageBilling && source === "EditMembershipPage"}
          />
        </div>
      )}
      </div>

      {isMember && !shouldHideMessages && (
        <MessagesHistoryPage memberId={membership?.id} />
      )}

      {/* Modal */}
      {/* <EditInvoiceModal
        isOpen={isEditModalOpen}
        isLoading={isLoading}
        invoice={selectedInvoice}
        onClose={() => setIsEditModalOpen(false)}
        onChange={setSelectedInvoice}
        onSubmit={handleEditInvoiceSubmit}
      /> */}
      <EditBillingInvoiceModal
        invoice={selectedInvoice}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onChange={setSelectedInvoice}
        onSubmit={handleEditInvoiceSubmit}
        isLoading={false}
        handelFetchMemberData={handelFetchMemberData}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        message={`Are you sure you want to delete the ${
          invoiceToDelete?.metadata?.credit ||
          invoiceToDelete?.metadata?.type === "credit"
            ? "payment"
            : "invoice"
        } "${
          invoiceToDelete?.description
        }" for ${formatCurrency(invoiceToDelete?.amount)}?`}
        onCancel={() => {
          setShowDeleteModal(false);
          setInvoiceToDelete(null);
        }}
        onConfirm={handleDelete}
        loading={deleting}
      />

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <Modal
          isOpen={showCreateInvoiceModal}
          onClose={() => setShowCreateInvoiceModal(false)}
          title="Create Invoice"
        >
          <CreateManualInvoice
            prefillMembershipId={membership?.id}
            disableMembershipField={true}
            onClose={() => setShowCreateInvoiceModal(false)}
            setIsCreatedmanualInvoice={setIsCreatedmanualInvoice}
            isCreatedManualInvoice={isCreatedManualInvoice}
            refreshApiCalls={refreshApiCalls}
            setRefreshApiCalls={setRefreshApiCalls}
          />
        </Modal>
      )}

      {/* Member self-pay modal — same PaymentModal flow the legacy
          BalanceDisplay card used, now driven from the unified balance card. */}
      {isMember && (
        <PaymentModal
          isOpen={showMemberPayModal}
          onClose={() => setShowMemberPayModal(false)}
          onPaymentSuccess={() => {
            setShowMemberPayModal(false);
            setRefreshApiCalls((prev) => prev + 1);
            fetchMembershipDetails();
          }}
        />
      )}

      {showPaymentModal && (
        <div>
          <SelectAdvancePaymentMethodInMember
            refreshApiCalls={refreshApiCalls}
            setRefreshApiCalls={setRefreshApiCalls}
            showPaymentModal={showPaymentModal}
            setShowPaymentModal={setShowPaymentModal}
          />
        </div>
      )}

      {/* Refund Modal */}
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setRefundInvoice(null);
        }}
        // A payment's line in Billing History is its CREDIT INVOICE; the
        // payment row behind it is `source_payment_id`. The line's own id is
        // never a payment id, so sending it made every refund answer "Payment
        // not found". The server also resolves a credit line's id to its
        // payment, for older lines written before source_payment_id existed.
        paymentId={refundInvoice?.source_payment_id || refundInvoice?.metadata?.payment_id || refundInvoice?.id || ""}
        invoiceId={refundInvoice?.id}
        paymentInvoiceId={refundInvoice?.id}
        linkedChargeInvoiceId={refundLinkedCharge?.id}
        linkedChargeAmount={refundLinkedCharge ? Number(refundLinkedCharge.amount) : undefined}
        linkedChargeDescription={refundLinkedCharge?.description}
        linkedRegistration={refundLinkedRegistration}
        onRefundSuccess={() => {
          fetch();
          fetchMembershipDetails();
          setRefreshApiCalls(prev => prev + 1);
        }}
      />
    </>
  );

  // The family's own /member/view-billing route renders with no `source`:
  // it gets the standard page frame (full width, PageHeading, one Card).
  // The staff invoice pages (EditDraftInvoicePage, GenerateInvoicePage)
  // also land in isViewBillingPage, so they keep the legacy wrapper below
  // untouched; converting those is the staff sweep's call (PM 4d6dbcfc).
  if (isViewBillingPage && !source) {
    return (
      <PageContainer>
        <PageHeading title={t('membership:billing.billingAndInvoices')} />
        <Card padding="md" border={false}>
          {billingContent}
        </Card>
      </PageContainer>
    );
  }

  // The staff invoice pages (EditDraftInvoicePage, GenerateInvoicePage) put
  // billing history under their own page heading. This used to draw a whole
  // second page there (min-h-screen, padding, a centered max-w-7xl column and
  // a second H1); it is a section of that page (PM 4d6dbcfc).
  if (isViewBillingPage) {
    return (
      <section className="mt-8">
        <SectionHeading>{t('membership:billing.billingAndInvoices')}</SectionHeading>
        <Card padding="md">{billingContent}</Card>
      </section>
    );
  }

  return billingContent;
};

export default MemberBillingHistory;
